import CodeMirror from '@uiw/react-codemirror';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactStars from 'react-rating-stars-component';
import Button from './Buttons';

import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { EditorView } from '@codemirror/view';
import { dracula } from '@uiw/codemirror-theme-dracula';
import { githubLight } from '@uiw/codemirror-theme-github';
import { nord } from '@uiw/codemirror-theme-nord';
import { okaidia } from '@uiw/codemirror-theme-okaidia';
import { Jodit } from 'jodit';
import { useAlert } from './AlertContext'; // Ensure this context exists for showAlert
import { useConfirmation } from './ConfirmationContext';

import { formatName } from '../utils/formatName';
import './CustomFormElements.css';

//================================= Customized Input with Toggle Visibility
interface CustomInputProps {
  id: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  value: string;
  defaultValue?: string;
  onChange?: (id: string, value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  icon?: React.ReactNode;
  type?: 'text' | 'password';
  toggleVisibility?: boolean;
  isVisible?: boolean | null;
  onVisibilityChange?: (isVisible: boolean) => void;
  className?: string;
  maxLengthValue?: number;
  readOnly?: boolean;
}

export const CustomInput = ({
  id,
  label,
  placeholder,
  disabled = false,
  value, // Controlled value
  defaultValue = '', // Default value for uncontrolled behavior
  onChange,
  onKeyDown,
  icon: Icon,
  type = 'text', // Native input type: 'text' or 'password'
  toggleVisibility = false,
  isVisible: externalVisibility = null,
  onVisibilityChange = null,
  className = '',
  maxLengthValue = 256,
  readOnly = false,
}: CustomInputProps) => {
  const [internalValue, setInternalValue] = useState<string>(defaultValue);
  const [internalVisibility, setInternalVisibility] = useState<boolean>(false);

  const isVisible = externalVisibility !== null ? externalVisibility : internalVisibility;

  // Sync external value when it changes
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  // Sync defaultValue when it changes
  useEffect(() => {
    setInternalValue(defaultValue);
  }, [defaultValue]);

  // Handle Value Change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (value === undefined) {
      setInternalValue(newValue); // Update internal state only if uncontrolled
    }

    if (onChange) {
      onChange(id, newValue); // Pass ID and value to parent for JSON collection
    }
  };

  // Handle Toggle Visibility
  const handleToggleVisibility = () => {
    if (externalVisibility === null) {
      setInternalVisibility((prev) => !prev);
    }
    if (onVisibilityChange) {
      onVisibilityChange(!isVisible);
    }
  };

  // Determine Input Type
  const inputType = toggleVisibility ? (isVisible ? 'text' : 'password') : type;

  // Determine Input Value
  const inputValue = value !== undefined ? value : internalValue;

  return (
    <div className={`form-group custom-text-input ${disabled ? 'disabled' : ''}`}>
      {/* Label */}
      {label && <label className="input-label">{label}</label>}

      {/* Input Wrapper */}
      <div className={`input-wrapper`}>
        <input
          id={id}
          type={inputType} // Use native type or dynamic type
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          disabled={disabled}
          className={`custom-input ${className ? className : ''}`}
          autoComplete="off"
          onKeyDown={onKeyDown}
          maxLength={maxLengthValue}
          readOnly={readOnly}
        />

        {/* Left Icon */}
        {Icon && <div className="input-icon">{Icon}</div>}

        {/* Password Toggle Button */}
        {toggleVisibility && (
          <button
            type="button"
            className={`password-toggle-btn ${isVisible ? 'visible' : 'invisible'}`}
            onClick={handleToggleVisibility}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

//================================================ Customized Radio Button
interface CustomRadioButtonProps {
  id: string;
  label?: string;
  name?: string;
  disabled?: boolean;
  value: string;
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const CustomRadioButton = ({
  id,
  label,
  name,
  disabled = false,
  value,
  checked,
  onChange,
}: CustomRadioButtonProps) => (
  <div className="form-group">
    <label htmlFor={id} className="custom-radio">
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange || (() => {})}
        disabled={disabled}
      />
      <span className="radio-mark"></span> {/* Visual radio circle */}
      {label && <span className="radio-label">{label}</span>}
    </label>
  </div>
);

interface CustomRadioGroupProps {
  name: string;
  options: CustomRadioButtonProps[];
  defaultValue?: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

export const CustomRadioGroup = ({
  name,
  options = [], // JSON array for radio buttons
  defaultValue = null, // Default selected radio button by value
  onChange, // Function called on change
  disabled = false, // Group-level disabled
}: CustomRadioGroupProps) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(defaultValue);

  // Handle change event
  const handleRadioChange = (value) => {
    setSelectedValue(value);
    if (onChange) {
      onChange(value); // Pass selected value to parent
    }
  };

  return (
    <div className="custom-radio-group">
      {options.map((option) => (
        <CustomRadioButton
          key={option.id}
          id={option.id}
          label={option.label}
          name={name}
          value={option.value}
          checked={selectedValue === option.value}
          disabled={disabled || option.disabled} // Group disabled overrides individual disabled
          onChange={() => handleRadioChange(option.value)}
        />
      ))}
    </div>
  );
};

//================================================ Customized Checkbox
interface CustomCheckboxProps {
  id?: string;
  name?: string;
  disabled?: boolean;
  checked?: boolean;
  defaultValue?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  containerClass?: string;
}
export const CustomCheckbox = ({
  id,
  name,
  disabled = false,
  checked, // Controlled behavior
  defaultValue = false, // Default initial state
  onChange,
  label,
  containerClass,
}: CustomCheckboxProps) => {
  const [isChecked, setIsChecked] = useState<boolean>(defaultValue);

  // Sync internal state with 'checked' prop if provided (controlled mode)
  useEffect(() => {
    if (checked !== undefined) {
      setIsChecked(checked);
    }
  }, [checked]);

  // Handle Change Event
  const handleCheckboxChange = (e) => {
    const newChecked = e.target.checked;
    setIsChecked(newChecked); // Update internal state

    if (onChange) {
      onChange(e); // Pass event back to parent component
    }
  };

  return (
    <div className={`checkbox-container ${containerClass || ''}`}>
      <input
        id={id || ''}
        type="checkbox"
        name={name}
        disabled={disabled}
        checked={isChecked}
        onChange={handleCheckboxChange}
      />
      {label && <label htmlFor={id || 'checkbox'}>{label}</label>}
    </div>
  );
};

//================================================ Customized Toggle Button
interface CustomToggleProps {
  id?: string;
  label?: string;
  name?: string;
  disabled?: boolean;
  checked?: boolean;
  defaultValue?: boolean;
  onChange?: (id: string, checked: boolean) => void;
}

export const CustomToggle = ({
  id,
  label,
  name,
  disabled = false,
  checked, // Controlled behavior
  defaultValue = false, // Default initial state
  onChange,
}: CustomToggleProps) => {
  const [isChecked, setIsChecked] = useState<boolean>(defaultValue);

  // Sync internal state with 'checked' prop if provided (controlled mode)
  useEffect(() => {
    if (checked !== undefined) {
      setIsChecked(checked);
    }
  }, [checked]);

  // Handle Toggle Change
  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = e.target.checked;
    setIsChecked(newChecked); // Update internal state

    if (onChange) {
      onChange(id, newChecked); // Pass id and newChecked value to parent
    }
  };

  return (
    <div className="form-group d-flex align-center">
      {/* Toggle Switch */}
      <label className="custom-toggle">
        <input
          id={id || ''}
          type="checkbox"
          name={name}
          checked={isChecked}
          onChange={handleToggleChange}
          disabled={disabled}
        />
        <span className="slider"></span>
      </label>
      {/* Label */}
      {label && (
        <label className="ml-2" htmlFor={id || 'toggle'}>
          {label}
        </label>
      )}
    </div>
  );
};

//==================== Customized Select Box with Single and Multi-Select Support
interface CustomSelectProps {
  id?: string;
  label?: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  value: string | string[];
  onChange?: (value: string | string[]) => void;
  multiSelect?: boolean;
  placeholder?: string;
  maxItems?: number;
}
export const CustomSelect = ({
  id, // Add id as a prop
  label,
  options,
  disabled,
  value,
  onChange,
  multiSelect = false,
  placeholder = 'Select options',
  maxItems = Infinity,
}: CustomSelectProps) => {
  const { showAlert } = useAlert();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedValues, setSelectedValues] = useState<string | string[]>(
    multiSelect ? (Array.isArray(value) ? value : []) : value || '',
  );

  const dropdownRef = useRef(null); // Reference for detecting outside click

  // Sync external value with local state when props change
  useEffect(() => {
    setSelectedValues(multiSelect ? (Array.isArray(value) ? value : []) : value || '');
  }, [value, multiSelect]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (!disabled) setIsOpen(!isOpen);
  };

  const handleOptionClick = (optionValue: string) => {
    if (multiSelect) {
      const updatedValues = selectedValues.includes(optionValue)
        ? (selectedValues as string[]).filter((val) => val !== optionValue)
        : [...selectedValues, optionValue];

      if (updatedValues.length > maxItems) {
        showAlert('fail', `You can select a maximum of ${maxItems} items.`);
        return;
      }

      setSelectedValues(updatedValues);
      onChange?.(updatedValues);
    } else {
      const updatedValue = selectedValues === optionValue ? null : optionValue;
      setSelectedValues(updatedValue);
      onChange?.(updatedValue);
    }
  };

  const isSelected = (optionValue: string) => {
    return multiSelect
      ? (selectedValues as string[]).includes(optionValue)
      : selectedValues === optionValue;
  };

  const removeSelectedValue = (optionValue: string) => {
    if (multiSelect) {
      const updatedValues = (selectedValues as string[]).filter((val) => val !== optionValue);
      setSelectedValues(updatedValues);
      onChange?.(updatedValues);
    }
  };

  return (
    <div
      id={id} // Attach the id here
      className={`form-group custom-select-wrapper ${disabled ? 'disabled' : ''}`}
      ref={dropdownRef}
    >
      {/* Label */}
      {label && <label className="select-label">{label}</label>}

      {/* Select Input */}
      <div className={`custom-select ${isOpen ? 'open' : ''}`} onClick={toggleDropdown}>
        <div
          className="select-display"
          data-value={
            multiSelect
              ? (selectedValues as string[]).join(',')
              : options.find((opt) => opt.value === selectedValues)?.value || ''
          }
        >
          {multiSelect ? (
            <div className="selected-tags">
              {selectedValues.length > 0 ? (
                (selectedValues as string[]).map((selectedValue) => {
                  const option = options.find((opt) => opt.value === selectedValue);
                  return (
                    <span key={selectedValue} className="selected-tag">
                      {option?.label}
                      <button
                        type="button"
                        className="remove-tag"
                        onClick={() => removeSelectedValue(selectedValue)}
                      >
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                    </span>
                  );
                })
              ) : (
                <span className="placeholder">{placeholder}</span>
              )}
            </div>
          ) : (
            options.find((opt) => opt.value === selectedValues)?.label || (
              <span className="placeholder">Select an option</span>
            )
          )}
        </div>
        <div className={`select-arrow ${isOpen ? 'open' : ''}`}>
          <i className="fa-solid fa-chevron-down"></i>
        </div>
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div className="custom-select-dropdown">
          <ul className="select-options">
            {options.map((option) => (
              <li
                key={option.value}
                className={`select-option ${isSelected(option.value) ? 'selected' : ''}`}
                onClick={() => handleOptionClick(option.value)}
              >
                <CustomCheckbox label={option.label} checked={isSelected(option.value)} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

//================================================ Customized Slider Box
interface CustomSliderProps {
  id?: string;
  label?: string;
  value?: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  oneStep?: number;
  suffix?: string;
}

export const CustomSlider = ({
  id, // Add id as a prop
  label,
  value = 0,
  onChange,
  disabled = false,
  min = 0,
  max = 100,
  oneStep = 1,
  suffix = '',
}: CustomSliderProps) => {
  const [currentValue, setCurrentValue] = useState<number>(value);

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(event.target.value);
    setCurrentValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  // Calculate percentage for progress
  const progressPercentage = ((currentValue - min) / (max - min)) * 100;

  return (
    <div
      id={id} // Attach id to the component
      className={`form-group custom-slider-container ${disabled ? 'disabled' : ''}`}
    >
      {/* Label */}
      {label && <label className="slider-label">{label}</label>}

      {/* Slider Wrapper */}
      <div className="slider-wrapper">
        {/* Min Label */}
        <span className="slider-min">
          {min}
          {suffix}
        </span>

        {/* Slider Track */}
        <div className="slider-track">
          <div className="slider-progress" style={{ width: `${progressPercentage}%` }} />
          <input
            type="range"
            min={min}
            max={max}
            step={oneStep}
            value={currentValue}
            onChange={handleSliderChange}
            disabled={disabled}
            className="custom-slider"
          />
          {/* Slider Thumb Display */}
          <div className="slider-thumb" style={{ left: `${progressPercentage}%` }}>
            {currentValue}
            {suffix}
          </div>
        </div>

        {/* Max Label */}
        <span className="slider-max">
          {max}
          {suffix}
        </span>
      </div>
    </div>
  );
};

//================================== Customized Number Input with Arrows
interface CustomNumberInputProps {
  id?: string;
  label?: string;
  value?: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
}

export const CustomNumberInput = ({
  id, // Add id as a prop
  label,
  value: initialValue = 0,
  onChange,
  disabled = false,
  min = 0,
  max = Infinity,
  step = 1,
}: CustomNumberInputProps) => {
  const [value, setValue] = useState<number>(Number(initialValue));

  // Sync external value changes with internal state
  useEffect(() => {
    setValue(Number(initialValue));
  }, [initialValue]);

  // Handle Increment
  const handleIncrement = () => {
    if (!disabled && value < max) {
      const newValue = value + step;
      setValue(newValue);
      onChange?.(newValue);
    }
  };

  // Handle Decrement
  const handleDecrement = () => {
    if (!disabled && value > min) {
      const newValue = value - step;
      setValue(newValue);
      onChange?.(newValue);
    }
  };

  // Handle Manual Input Change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    if (!isNaN(newValue) && newValue >= min && newValue <= max) {
      setValue(newValue);
      onChange?.(newValue);
    }
  };

  return (
    <div
      id={id} // Attach the id to the component
      className={`form-group custom-number-input-container ${disabled ? 'disabled' : ''}`}
    >
      {/* Label */}
      {label && <label className="number-input-label">{label}</label>}

      {/* Number Input Wrapper */}
      <div className="number-input-wrapper">
        {/* Decrement Button */}
        <button
          type="button"
          className="number-input-button decrement"
          onClick={handleDecrement}
          disabled={disabled || value <= min}
        >
          <i className="fa-solid fa-minus"></i>
        </button>

        {/* Number Input Display */}
        <input
          type="number"
          className="number-input-display"
          value={value}
          onChange={handleInputChange}
          disabled={disabled}
          min={min}
          max={Number.isFinite(max) ? max : undefined}
          step={step}
        />

        {/* Increment Button */}
        <button
          type="button"
          className="number-input-button increment"
          onClick={handleIncrement}
          disabled={disabled || value >= max}
        >
          <i className="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>
  );
};

//================================== Customized Star Rating Box
interface CustomCircleRatingProps {
  id?: string;
  label?: string;
  value?: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
  isHalf?: boolean;
  mandatory?: boolean;
}

export const CustomCircleRating = ({
  id, // Add id as a prop
  label,
  value = 0,
  onChange,
  disabled = false,
  isHalf = true,
  mandatory = false,
}: CustomCircleRatingProps) => {
  return (
    <div
      id={id} // Attach the id to the component's outer div
      className={`form-group custom-circle-rating ${disabled ? 'disabled' : ''}`}
    >
      {/* Label */}
      {label && (
        <label className="rating-label">
          {label} {mandatory && <span className="mandatory">*</span>}
        </label>
      )}

      {/* Circle Rating */}
      <ReactStars
        count={5}
        value={value}
        onChange={onChange}
        size={30}
        isHalf={false}
        edit={!disabled}
        color="var(--bs-secondary, #dcdcdd)" /* Inactive circle color */
        activeColor="var(--bs-primary, #7b57e0)" /* Active circle color */
        emptyIcon={<div className="circle-icon empty"></div>}
        halfIcon={<div className="circle-icon half"></div>}
        filledIcon={<div className="circle-icon filled"></div>}
      />
    </div>
  );
};

//================================== Customized WYSIWYG Editor Component

//  * Custom WYSIWYG Editor Component
//  * @param {string} label - Label for the editor.
//  * @param {boolean} mandatory - Marks the editor as mandatory.
//  * @param {boolean} disabled - Disables the editor.
//  * @param {string} addedText - Pre-filled text content.
//  * @param {boolean} resizable - Enables resizing of the editor.
//  * @param {string|number} height - Default height of the editor.
//  * @param {function} onChange - Callback when the content changes.
interface CustomWYSIWYGEditorProps {
  id?: string;
  label?: string;
  sublabel?: string;
  mandatory?: boolean;
  disabled?: boolean;
  addedText?: string;
  resizable?: boolean;
  height?: string | number;
  onChange?: (content: string) => void;
}

export const CustomWYSIWYGEditor = ({
  id, // Add id prop
  label,
  sublabel,
  mandatory = false,
  disabled = false,
  addedText = '',
  resizable = true,
  height = 400,
  onChange,
}: CustomWYSIWYGEditorProps) => {
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const editorInstance = useRef<Jodit | null>(null);

  useEffect(() => {
    if (!editorInstance.current) {
      editorInstance.current = Jodit.make(editorRef.current, {
        readonly: disabled,
        height: height,
        allowResizeX: false,
        allowResizeY: resizable,
        placeholder: addedText,
        toolbarSticky: true,
        toolbarAdaptive: true,
        toolbarButtonSize: 'middle',
        theme: 'light',
        spellcheck: true,
        language: 'auto',
        tabIndex: -1,
        toolbar: true,
        colors: {
          greyscale: [
            '#000000',
            '#434343',
            '#666666',
            '#999999',
            '#B7B7B7',
            '#CCCCCC',
            '#D9D9D9',
            '#EFEFEF',
            '#F3F3F3',
            '#FFFFFF',
          ],
          palette: [
            '#980000',
            '#FF0000',
            '#FF9900',
            '#FFFF00',
            '#00F0F0',
            '#00FFFF',
            '#4A86E8',
            '#0000FF',
            '#9900FF',
            '#FF00FF',
          ],
          full: [
            '#E6B8AF',
            '#F4CCCC',
            '#FCE5CD',
            '#FFF2CC',
            '#D9EAD3',
            '#D0E0E3',
            '#C9DAF8',
            '#CFE2F3',
            '#D9D2E9',
            '#EAD1DC',
            '#DD7E6B',
            '#EA9999',
            '#F9CB9C',
            '#FFE599',
            '#B6D7A8',
            '#A2C4C9',
            '#A4C2F4',
            '#9FC5E8',
            '#B4A7D6',
            '#D5A6BD',
            '#CC4125',
            '#E06666',
            '#F6B26B',
            '#FFD966',
            '#93C47D',
            '#76A5AF',
            '#6D9EEB',
            '#6FA8DC',
            '#8E7CC3',
            '#C27BA0',
            '#A61C00',
            '#CC0000',
            '#E69138',
            '#F1C232',
            '#6AA84F',
            '#45818E',
            '#3C78D8',
            '#3D85C6',
            '#674EA7',
            '#A64D79',
            '#85200C',
            '#990000',
            '#B45F06',
            '#BF9000',
            '#38761D',
            '#134F5C',
            '#1155CC',
            '#0B5394',
            '#351C75',
            '#733554',
            '#5B0F00',
            '#660000',
            '#783F04',
            '#7F6000',
            '#274E13',
            '#0C343D',
            '#1C4587',
            '#073763',
            '#20124D',
            '#4C1130',
          ],
        },
        colorPickerDefaultTab: 'background',
        activeButtonsInReadOnly: ['source', 'fullsize', 'print', 'about', 'dots'],
        removeButtons: [],
        disablePlugins: [],
        extraButtons: [],
        sizeLG: 900,
        sizeMD: 700,
        sizeSM: 400,
        buttons: [
          'bold',
          'underline',
          'italic',
          'ul',
          'ol',
          'font',
          'fontsize',
          'brush',
          'paragraph',
          'image',
          'table',
          'link',
          'undo',
          'redo',
        ],
        buttonsXS: [
          'bold',
          'image',
          '|',
          'brush',
          'paragraph',
          '|',
          'align',
          '|',
          'undo',
          'redo',
          '|',
          'eraser',
          'dots',
        ],
        uploader: {
          insertImageAsBase64URI: true,
        },
        filebrowser: {
          ajax: {
            url: '/files',
          },
        },
        defaultActionOnPaste: 'insert_clear_html',
      });

      editorInstance.current.value = addedText;

      editorInstance.current.events.on('change', () => {
        const content = editorInstance.current.value;
        if (onChange) {
          onChange(content);
        }
      });
    }

    return () => {
      if (editorInstance.current) {
        editorInstance.current.destruct();
        editorInstance.current = null;
      }
    };
  }, [disabled, height, resizable, addedText, onChange]);

  return (
    <div
      id={id} // Add the id to the container div
      className={`form-group custom-wysiwyg-editor ${disabled ? 'disabled' : ''}`}
    >
      {/* Label */}
      {label && (
        <label className="editor-label">
          {label} {mandatory && <span className="mandatory">*</span>}
          <div className="wysiwyg-sublabel">{sublabel}</div>
        </label>
      )}

      {/* Editor */}
      <textarea ref={editorRef}></textarea>
    </div>
  );
};

//================================== Customized Code block

const languageExtensions = {
  python: python(),
  javascript: javascript({ jsx: true }),
  html: html(),
  css: css(),
};

// Define theme mapping
const themeMap = {
  dracula,
  githubLight,
  okaidia,
  nord,
};

interface CustomCodeEditorProps {
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  language?: string;
  editable?: boolean;
  boxtitle?: string;
  height?: string;
  highlightActiveLine?: boolean;
  lightTheme?: string;
  darkTheme?: string;
  wrapText?: boolean;
  lineNumbers?: boolean;
  foldGutter?: boolean;
}

export const CustomCodeEditor = ({
  id, // Add id as a prop
  value = '',
  onChange = () => {},
  language = 'javascript',
  editable = true,
  boxtitle = 'Custom Code Editor',
  height = '400px',
  highlightActiveLine = false,
  lightTheme = 'githubLight',
  darkTheme = 'dracula',
  wrapText = false,
  lineNumbers = true,
  foldGutter = true,
}: CustomCodeEditorProps) => {
  const [code, setCode] = useState<string>(value);
  const [editorTheme, setEditorTheme] = useState<'light' | 'dark'>(
    document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
  );
  const { showAlert } = useAlert();

  // Set initial theme on load
  useEffect(() => {
    const htmlTheme = document.documentElement.getAttribute('data-theme');
    setEditorTheme(htmlTheme === 'dark' ? 'dark' : 'light');
  }, []);

  // Observe for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const htmlTheme = document.documentElement.getAttribute('data-theme');
      setEditorTheme(htmlTheme === 'dark' ? 'dark' : 'light');
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  // Handle Code Change
  const handleCodeChange = (newValue: string) => {
    setCode(newValue);
    onChange(newValue);
  };

  // Handle Copy Code
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      showAlert('success', 'Code has been copied to the clipboard!');
    } catch {
      showAlert('fail', 'Failed to copy the code.');
    }
  };

  // Select Language Extension
  const selectedLanguageExtension = useMemo(() => {
    return languageExtensions[language] || javascript();
  }, [language]);

  // Resolve Theme Dynamically
  const selectedTheme = useMemo(() => {
    return editorTheme === 'dark'
      ? themeMap[darkTheme] || dracula
      : themeMap[lightTheme] || githubLight;
  }, [editorTheme, lightTheme, darkTheme]);

  // Dynamic Wrapping Extension
  const extensions = useMemo(() => {
    const baseExtensions = [selectedLanguageExtension];
    if (wrapText) {
      baseExtensions.push(EditorView.lineWrapping);
    }
    return baseExtensions;
  }, [selectedLanguageExtension, wrapText]);

  return (
    <div id={id} className="custom-code-editor">
      {' '}
      {/* Attach id here */}
      {/* Header with Copy Button */}
      <div className="editor-header">
        <span className="editor-title">{boxtitle}</span>
        <Button variant="primary" size="small" onClick={handleCopyCode}>
          Copy
        </Button>
      </div>
      {/* Code Editor */}
      <CodeMirror
        key={editorTheme} // Re-initialize editor when theme changes
        value={code}
        height={height}
        theme={selectedTheme}
        extensions={extensions}
        onChange={handleCodeChange}
        editable={editable}
        basicSetup={{
          lineNumbers: lineNumbers,
          foldGutter: foldGutter,
          highlightActiveLine: highlightActiveLine,
          highlightActiveLineGutter: highlightActiveLine,
          highlightSelectionMatches: false,
          tabSize: 2,
        }}
      />
    </div>
  );
};

// =========================================== Text area component
interface CustomTextareaProps {
  id: string;
  label?: string;
  sublabel?: string;
  mandatory?: boolean;
  disabled?: boolean;
  addedText?: string;
  resizable?: boolean;
  height?: string | number;
  onChange?: (content: string) => void;
  onDragOver?: (e: React.DragEvent<HTMLTextAreaElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLTextAreaElement>) => void;
  readOnly?: boolean;
  name?: string;
}

export const CustomTextarea = ({
  id, // Unique ID for the textarea
  label, // Label for the textarea
  sublabel, // Sublabel (supports HTML content)
  mandatory = false, // Mark the field as mandatory
  disabled = false, // Disable the textarea
  addedText = '', // Pre-filled text content
  resizable = true, // Allow resizing of the textarea
  height = '300px', // Default height of the textarea
  onChange, // Callback for content change
  onDragOver, // Callback for drag over event
  onDrop, // Callback for drop event
  readOnly = false, // Make the textarea read-only
  name, // Name attribute for the textarea
}: CustomTextareaProps) => {
  const [content, setContent] = useState<string>(addedText);

  // Sync external `addedText` when it changes
  useEffect(() => {
    setContent(addedText);
  }, [addedText]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setContent(newValue);
    if (onChange) {
      onChange(newValue); // Trigger callback with the updated value
    }
  };

  return (
    <div
      id={id} // Attach the unique ID to the outer container
      className={`form-group custom-textarea ${disabled ? 'disabled' : ''}`}
    >
      {/* Label */}
      {label && (
        <label className="textarea-label">
          {label} {mandatory && <span className="mandatory">*</span>}
        </label>
      )}
      {/* Sublabel */}
      {sublabel && (
        <div className="textarea-sublabel" dangerouslySetInnerHTML={{ __html: sublabel }}></div>
      )}
      {/* Textarea */}
      <textarea
        id={`${id}-textarea`}
        className="textarea"
        style={{
          resize: resizable ? 'both' : 'none',
          height: typeof height === 'number' ? `${height}px` : height,
        }}
        value={content}
        onChange={handleContentChange}
        onDragOver={onDragOver} // Attach drag over event handler
        onDrop={onDrop} // Attach drop event handler
        disabled={disabled}
        readOnly={readOnly}
        name={name}
      />
    </div>
  );
};

export default CustomTextarea;

// =========================================== File upload component

interface CustomFileUploadProps {
  multiple?: boolean;
  maxFileSize?: number;
  allowedFormats?: string[];
  allowedExtensions?: string[];
  showUploadedFiles?: boolean;
  allowFileDeletion?: boolean;
  onFileSelect?: (files: File[]) => void;
}

export const CustomFileUpload = ({
  multiple = true,
  maxFileSize = 5 * 1024 * 1024,
  allowedFormats = ['image/png', 'image/jpeg', 'application/pdf', 'image/heic'],
  allowedExtensions = [], // allowedExtensions for UI display (more user-friendly, e.g. ".pdf")
  showUploadedFiles = true,
  allowFileDeletion = true,
  onFileSelect, // Callback function to pass selected files
}: CustomFileUploadProps) => {
  const { showConfirmation } = useConfirmation();
  const { showAlert } = useAlert();

  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use allowedExtensions for UI if available; otherwise fallback to allowedFormats (MIME)
  const displayFormats = allowedExtensions?.length ? allowedExtensions : allowedFormats;

  const createUpdatedFile = ({
    file,
    name,
    type,
  }: {
    file: File;
    name?: string;
    type?: string;
  }) => {
    const newName = name ?? file.name;
    const newType = type ?? file.type;

    return new File([file], newName, {
      type: newType,
      lastModified: file.lastModified,
    });
  };
  const getFileExtension = (name: string): string => {
    const idx = name.lastIndexOf('.');
    return idx >= 0 ? name.slice(idx).toLowerCase() : '';
  };

  const isExtensionAllowed = (name: string): boolean => {
    if (!allowedExtensions?.length) return false;
    const ext = getFileExtension(name);
    return allowedExtensions
      .map((e) => (e.startsWith('.') ? e : `.${e}`).toLowerCase())
      .includes(ext);
  };

  const validateFile = async (file: File): Promise<File | null> => {
    console.log('Validating file:', file.name, 'Type:', file.type, 'Size:', file.size);

    if (file.type === '') {
      try {
        const buffer = await file.slice(0, 12).arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const ascii = String.fromCharCode(...bytes);

        if (ascii.substring(4, 8) === 'ftyp') {
          const subtype = ascii.substring(8, 12);

          if (subtype === 'heic') {
            file = createUpdatedFile({
              file: file,
              type: 'image/heic',
            });
          }
        }
      } catch (error) {
        console.error('Error reading file signature:', error);
      }
    }

    // Accept if MIME matches allowed formats, or (when MIME is missing/unknown) the extension matches.
    const mimeOk = allowedFormats.includes(file.type);
    const extOk = isExtensionAllowed(file.name);

    if (!mimeOk && !extOk) {
      console.log(
        `Invalid format. Actual: "${file.type || 'unknown'}" Allowed: ${displayFormats.join(', ')}`,
      );
      showAlert('warning', `Invalid format. Allowed: ${displayFormats.join(', ')}`);
      return null;
    }

    return file;
  };

  const validateTotalSize = (files: File[]): boolean => {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > maxFileSize) {
      console.log(
        `Total file size (${(totalSize / 1024 / 1024).toFixed(1)} MB) exceeds the limit of ${maxFileSize / 1024 / 1024} MB`,
      );

      showAlert(
        'warning',
        `Total file size (${(totalSize / 1024 / 1024).toFixed(1)} MB) exceeds the limit of ${maxFileSize / 1024 / 1024} MB`,
      );

      return false;
    }
    return true;
  };

  // Merge new files with existing ones while ensuring uniqueness based on name, size, and lastModified
  const mergeUniqueFiles = (prevFiles: File[], newFiles: File[]): File[] =>
    Array.from(
      new Map(
        [...prevFiles, ...newFiles].map((file) => [
          `${file.name}-${file.size}-${file.lastModified}`,
          file,
        ]),
      ).values(),
    );

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileArray = Array.from(event.target.files);
    const validatedFiles = await Promise.all(fileArray.map(validateFile));
    const selectedFiles = validatedFiles.filter(Boolean);

    setFiles((prev) => {
      const updatedFiles = multiple
        ? mergeUniqueFiles(prev, selectedFiles)
        : selectedFiles.slice(0, 1);

      if (!validateTotalSize(updatedFiles)) {
        return prev; // keep previous files unchanged
      }

      if (onFileSelect) {
        onFileSelect(updatedFiles);
      }

      return updatedFiles;
    });

    event.target.value = ''; // Clear the input to allow re-uploading the same file if needed
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const fileArray = Array.from(e.dataTransfer.files);
    const validationResults = await Promise.all(fileArray.map(validateFile));
    const droppedFiles = fileArray.filter((_, index) => validationResults[index]);

    setFiles((prev) => {
      const updatedFiles = multiple
        ? mergeUniqueFiles(prev, droppedFiles)
        : droppedFiles.slice(0, 1);

      if (onFileSelect) {
        onFileSelect(updatedFiles);
      }

      return updatedFiles;
    });
  };

  const handleDelete = (fileToDelete: File) => {
    showConfirmation('Are you sure you want to delete this file?', () => {
      setFiles((prev) => {
        const updatedFiles = prev.filter(
          (file) =>
            !(
              file.name === fileToDelete.name &&
              file.size === fileToDelete.size &&
              file.lastModified === fileToDelete.lastModified
            ),
        );

        if (onFileSelect) {
          onFileSelect(updatedFiles);
        }

        return updatedFiles;
      });
    });
  };

  return (
    <div className="custom-file-upload">
      <div
        className={`custom-file-upload-dropzone ${isDragging ? 'dragging' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <label className="custom-file-upload-label">
          Drag & drop files or <span className="custom-file-upload-button">Browse</span>
        </label>
        <p className="custom-file-upload-info">
          Max {`${files.length > 1 ? 'files' : 'file'}`} size: {maxFileSize / 1024 / 1024} MB |
          Allowed: {displayFormats.join(', ')}
        </p>
      </div>

      {showUploadedFiles && files.length > 0 && (
        <div className="custom-file-upload-list">
          <h4>Uploaded Files:</h4>
          <ul>
            {files.map((file, index) => (
              <li key={index} className="custom-file-upload-item">
                <span>
                  {formatName(file.name)} - {(file.size / 1024).toFixed(2)} KB
                </span>
                {allowFileDeletion && (
                  <button className="btn btn-danger btn-small" onClick={() => handleDelete(file)}>
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// =========================================== Upload Image Editor
